import {createSelector} from '@reduxjs/toolkit';

import {APPLE_PAY_LABELS} from 'constants/applePay';
import {PAYMENTS} from 'desktop/pages/checkout/constants/paymentStep';
import {ApplicationStoreType} from 'desktop/store';
import {isUhcEnabled} from 'libs/rollout';
import {PaymentDetailsLineType, PaymentDetailsType} from 'types/applePayType';

import {ApplePayType} from '../../../reducers/paymentStep/applePay/applePay';
import {getCompleteOrderFirstError} from '../../completeOrder/completeOrder';
import {MODULE_KEY_CHECKOUT} from '../../constants';
import {getOrderReviewItems} from '../../orderReviewItems/orderReviewItems';
import {getOrderReviewTotals} from '../../orderReviewTotals/orderReviewTotals';
import {getMessageError, getNameSelectedMethod} from '../paymentStep';

import {paymentDetailsItemStructure} from './helpers';

/**
 * Get Apple Pay data
 *
 * @param {ApplicationStoreType} store - current store
 * @return {ApplePayType} - apple pay data
 */
const getApplePay = (store: ApplicationStoreType): ApplePayType =>
    store[MODULE_KEY_CHECKOUT].paymentStep.applePay;

/**
 * Get is payment method loading
 *
 * @param {ApplicationStoreType} store - current store
 * @return {boolean} - payment method is loading
 */
export const getApplePayIsLoading = createSelector(getApplePay, applePay => applePay.loading);

/**
 * Get inner Apple Pay error
 *
 * @param {ApplicationStoreType} store - current store
 * @return {string} - error message
 */
export const getApplePayInnerError = createSelector(getApplePay, applePay => applePay.errorMessage);

/**
 * Collects data about items for payment request details
 * @param {ApplicationStoreType} store - Current store
 * @returns {Array<PaymentDetailsLineType>} - items for payment request details
 */
export const getPaymentDetailsItems = createSelector(
    getOrderReviewItems,
    (orderReviewItems): Array<PaymentDetailsLineType> => {
        return orderReviewItems.map(({name, total}) => paymentDetailsItemStructure(name, total));
    },
);

/**
 * Collects payment request details without shipping options
 * @param {ApplicationStoreType} store - Current store
 * @returns {PaymentDetailsType} - payment details info for payment request, shipping data is not included
 */
export const getPaymentDetailsWithoutShipping = createSelector(
    getOrderReviewTotals,
    getPaymentDetailsItems,
    ({copay = '0', discount, grandTotal, shipping, totalTax = 0}, items): PaymentDetailsType => {
        let displayItems = [
            ...items,
            paymentDetailsItemStructure(APPLE_PAY_LABELS.SHIPPING, shipping.price),
        ];
        if (isUhcEnabled())
            displayItems = [
                ...displayItems,
                paymentDetailsItemStructure(APPLE_PAY_LABELS.COPAY, Number(copay)),
            ];
        if (Number(discount?.price))
            displayItems = [
                ...displayItems,
                paymentDetailsItemStructure(
                    APPLE_PAY_LABELS.DISCOUNT,
                    Number(`-${discount.price}`),
                ),
            ];
        displayItems = [
            ...displayItems,
            paymentDetailsItemStructure(APPLE_PAY_LABELS.TAX, totalTax),
        ];
        return {
            displayItems,
            total: paymentDetailsItemStructure(APPLE_PAY_LABELS.TOTAL, grandTotal),
        };
    },
);

/**
 * Get error for Apple pay from save/saveOrder
 *
 * @param {ApplicationStoreType} store - current store
 * @return {string} - error message
 */
export const getApplePayOrderError = createSelector(
    getMessageError,
    getCompleteOrderFirstError,
    getNameSelectedMethod,
    (messageError, orderError, selectedMethod) => {
        if (selectedMethod === PAYMENTS.METHOD_APPLE_PAY) {
            return messageError || orderError || '';
        }
        return '';
    },
);
